/**
 * The single wire shape for a file, emitted by every vingapi response that
 * references an uploaded asset (`FileRef` in the OpenAPI schema).
 *
 * `null` in place of a FileRef means exactly "no file". A file that exists but
 * is not usable yet is a FileRef with `status !== 'ready'` and null URLs —
 * never an empty string, never a missing field.
 */
export type FileRefStatus = 'processing' | 'ready' | 'failed';

/**
 * Wire-facing failure reason: `FileRef.error` (non-null iff status is failed)
 * and the `code` of a 422 decline from the consume accept endpoint. Mirrors
 * vingapi `FileRefError` — the four post-acceptance codes plus the synchronous
 * declines (file-handling plan A3 rows 1–9).
 */
export type FileRefError =
  | 'virus_detected'
  | 'upload_missing'
  | 'transcode_failed'
  | 'processing_failed'
  | 'type_not_allowed'
  | 'empty_file'
  | 'too_large'
  | 'upload_incomplete'
  | 'type_mismatch'
  | 'image_unreadable'
  | 'image_too_large'
  | 'heic_invalid'
  | 'pdf_invalid';

export type FileRefType = 'image' | 'video' | 'audio' | 'document' | 'other';

export interface FileRef {
  status: FileRefStatus;
  /** Why the file failed; non-null iff `status === 'failed'`. */
  error?: FileRefError | null;
  type: FileRefType;
  /** Original upload basename; present in every status. */
  filename: string;
  /** Size in bytes; null for legacy rows. */
  size?: number | null;
  /** Page count for documents; null when unknown or not a document. */
  pageCount?: number | null;
  /** The surface's primary rendition; non-null iff `status === 'ready'`. */
  url?: string | null;
  /** Thumbnail rendition, where the surface uses one. */
  thumbnailUrl?: string | null;
  /** Attachment-disposition URL, where the surface uses one. */
  downloadUrl?: string | null;
}

/**
 * Exhaustive key map for the hand-maintained FileRef — adding a field to the
 * interface without adding it here is a type error, and the guard test
 * (`fileRefWireShape.guard.test.ts`) asserts these keys are a subset of the
 * wire schema's camel-cased keys.
 */
export const FILE_REF_KEYS: Record<keyof FileRef, true> = {
  status: true,
  error: true,
  type: true,
  filename: true,
  size: true,
  pageCount: true,
  url: true,
  thumbnailUrl: true,
  downloadUrl: true,
};
